import React from "react";
import { fireEvent, render, screen } from "@testing-library/react";
import { useTranscripts } from "../../hooks/useTranscripts";
import TranscriptList from "./TranscriptList";

jest.mock("react-router-dom", () => ({
  ...jest.requireActual("react-router-dom"),
  useNavigate: () => jest.fn(),
}));

jest.mock("../../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));

jest.mock("../../hooks/useTranscripts");

const mockedUseTranscripts = jest.mocked(useTranscripts);

const folder = {
  folder_id: 1,
  name: "Research",
  parent_folder_id: null,
  created_by_user_id: "user-1",
  created_date: "2026-10-01T00:00:00Z",
  modified_date: "2026-10-01T00:00:00Z",
};

const unfiledTranscript = {
  transcript_id: 10,
  title: "Weekly interview notes",
  status: "Completed",
  visibility: "Private",
  language: "en-US",
  folder_id: null,
  created_by_user_id: "user-1",
  created_date: "2026-10-18T00:00:00Z",
  modified_date: "2026-10-18T00:00:00Z",
  segments: [],
};

const transcriptInResearch = {
  ...unfiledTranscript,
  transcript_id: 11,
  title: "Older folder interview notes",
  folder_id: 1,
};

const failedUnfiledTranscript = {
  ...unfiledTranscript,
  transcript_id: 12,
  title: "Interrupted recording",
  status: "Failed",
};

const recentTranscriptInResearch = {
  ...transcriptInResearch,
  transcript_id: 12,
  title: "Recent folder interview notes",
  created_date: "2026-10-19T00:00:00Z",
};

const nestedFolder = {
  ...folder,
  folder_id: 2,
  name: "Interviews",
  parent_folder_id: 1,
};

const transcriptInNestedFolder = {
  ...transcriptInResearch,
  transcript_id: 13,
  title: "Nested folder notes",
  folder_id: 2,
};

describe("TranscriptList", () => {
  beforeEach(() => {
    mockedUseTranscripts.mockReturnValue({
      createFolder: jest.fn(),
      deleteFolder: jest.fn(),
      deleteTranscript: jest.fn(),
      listFolders: jest.fn().mockResolvedValue([folder]),
      listTranscripts: jest.fn().mockResolvedValue([unfiledTranscript]),
      previewDeleteFolder: jest.fn(),
      updateBatchVisibility: jest.fn(),
      updateFolder: jest.fn(),
      updateTranscript: jest.fn(),
    } as unknown as ReturnType<typeof useTranscripts>);
  });

  it("shows unfiled transcripts below folders in one list", async () => {
    render(<TranscriptList />);

    expect(await screen.findByText("Weekly interview notes")).not.toBeNull();
    expect(screen.getByText("Research")).not.toBeNull();
    expect(screen.getByText("Unfiled transcripts")).not.toBeNull();
    expect(screen.queryByText("All transcripts")).toBeNull();
    expect(
      screen.getByRole("button", { name: "Sort transcripts alphabetically" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Visibility Weekly interview notes" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Move Weekly interview notes" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Open Weekly interview notes" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Delete Weekly interview notes" }),
    ).not.toBeNull();
  });

  it("counts foldered and failed transcripts in the workspace total", async () => {
    mockedUseTranscripts.mockReturnValue({
      createFolder: jest.fn(),
      deleteFolder: jest.fn(),
      deleteTranscript: jest.fn(),
      listFolders: jest.fn().mockResolvedValue([folder]),
      listTranscripts: jest
        .fn()
        .mockResolvedValue([
          unfiledTranscript,
          transcriptInResearch,
          failedUnfiledTranscript,
        ]),
      previewDeleteFolder: jest.fn(),
      updateBatchVisibility: jest.fn(),
      updateFolder: jest.fn(),
      updateTranscript: jest.fn(),
    } as unknown as ReturnType<typeof useTranscripts>);

    render(<TranscriptList />);

    expect(
      await screen.findByText("3 transcripts in current workspace"),
    ).not.toBeNull();
    expect(screen.getByText("Interrupted recording")).not.toBeNull();
  });

  it("shows a failed transcript with its delete action and no retry", async () => {
    mockedUseTranscripts.mockReturnValue({
      createFolder: jest.fn(),
      deleteFolder: jest.fn(),
      deleteTranscript: jest.fn(),
      listFolders: jest.fn().mockResolvedValue([]),
      listTranscripts: jest.fn().mockResolvedValue([
        {
          ...unfiledTranscript,
          title: "Interrupted recording",
          status: "Failed",
        },
      ]),
      previewDeleteFolder: jest.fn(),
      updateBatchVisibility: jest.fn(),
      updateFolder: jest.fn(),
      updateTranscript: jest.fn(),
    } as unknown as ReturnType<typeof useTranscripts>);

    render(<TranscriptList />);

    expect(await screen.findByText(/Interrupted recording/)).not.toBeNull();
    expect(screen.getByText(/Failed/)).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Delete Interrupted recording" }),
    ).not.toBeNull();
    expect(screen.queryByRole("button", { name: /retry/i })).toBeNull();
  });

  it("shows each folder transcript only while its folder is expanded", async () => {
    mockedUseTranscripts.mockReturnValue({
      createFolder: jest.fn(),
      deleteFolder: jest.fn(),
      deleteTranscript: jest.fn(),
      listFolders: jest.fn().mockResolvedValue([folder, nestedFolder]),
      listTranscripts: jest
        .fn()
        .mockResolvedValue([
          unfiledTranscript,
          transcriptInResearch,
          recentTranscriptInResearch,
          transcriptInNestedFolder,
        ]),
      previewDeleteFolder: jest.fn(),
      updateBatchVisibility: jest.fn(),
      updateFolder: jest.fn(),
      updateTranscript: jest.fn(),
    } as unknown as ReturnType<typeof useTranscripts>);

    render(<TranscriptList />);

    expect(await screen.findByText("Unfiled transcripts")).not.toBeNull();
    expect(screen.getByText("2 transcripts · 1 folder")).not.toBeNull();
    expect(screen.getByText("Older folder interview notes")).not.toBeNull();
    expect(screen.getByText("Recent folder interview notes")).not.toBeNull();
    expect(screen.getByText("Interviews")).not.toBeNull();
    expect(screen.getByText("Nested folder notes")).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Open Older folder interview notes" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", {
        name: "Visibility Older folder interview notes",
      }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", { name: "Move Older folder interview notes" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", {
        name: "Delete Older folder interview notes",
      }),
    ).not.toBeNull();
    const recentTitle = screen.getByText("Recent folder interview notes");
    const olderTitle = screen.getByText("Older folder interview notes");
    expect(
      Boolean(
        recentTitle.compareDocumentPosition(olderTitle) &
        Node.DOCUMENT_POSITION_FOLLOWING,
      ),
    ).toBe(true);

    fireEvent.click(screen.getByRole("button", { name: "Collapse Research" }));
    expect(screen.queryByText("Older folder interview notes")).toBeNull();
    expect(screen.queryByText("Nested folder notes")).toBeNull();

    fireEvent.click(screen.getByRole("button", { name: "Expand Research" }));
    expect(
      await screen.findByText("Older folder interview notes"),
    ).not.toBeNull();
    expect(await screen.findByText("Nested folder notes")).not.toBeNull();
  });

  it("keeps the full current title visible in a compact inline rename editor", async () => {
    render(<TranscriptList />);

    fireEvent.click(
      await screen.findByRole("button", { name: "Weekly interview notes" }),
    );
    const input = await screen.findByRole("textbox", {
      name: "Rename Weekly interview notes",
    });
    expect((input as HTMLInputElement).value).toBe("Weekly interview notes");
    expect(
      screen.getByRole("button", { name: "Save Weekly interview notes" }),
    ).not.toBeNull();
    expect(
      screen.getByRole("button", {
        name: "Cancel renaming Weekly interview notes",
      }),
    ).not.toBeNull();
  });

  it("opens the inline editor when a folder rename action is selected", async () => {
    render(<TranscriptList />);

    fireEvent.click(
      await screen.findByRole("button", { name: "Rename Research" }),
    );
    const input = await screen.findByRole("textbox", {
      name: "Rename Research",
    });
    expect((input as HTMLInputElement).value).toBe("Research");
  });
});
